import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { LinkedInSolid } from './icons/LinkedInSolid';
import NewsletterSignup from './NewsletterSignup';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinksCol1 = [
    { label: 'About', href: '/about' },
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'People', href: '/team' },
    { label: 'Founders', href: '/founders' },
    { label: 'Investors', href: '/investors' },
  ];
  const quickLinksCol2 = [
    { label: 'Insights', href: '/insights' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
    { label: 'SEBI Registration Details', href: '/sebi-registration-details' },
  ];

  return (
    <footer className="bg-[#211F1F] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Brand Column */}
          <div className="flex flex-col">
            <div className="bg-[#211F1F] w-fit mb-0 overflow-visible">
              <img
                src="/images/yournest-logo-footer.png?v=20260224d"
                alt="YourNest Venture Capital"
                className="h-24 md:h-28 w-auto object-contain object-left mix-blend-screen lg:-translate-x-1/4"
              />
            </div>
            <p className="text-white mb-3 max-w-sm">
              YourNest is a sharply focused early-stage DeepTech VC fund from India.
            </p>
            <p className="text-white text-sm mb-1">YourNest Capital Advisors Pvt Ltd</p>
            <p className="text-white text-sm mb-1">CIN No.: U67200HR2011PTC042643</p>
            <p className="text-white text-sm mb-6">GST No.: 06AAACY4785F1Z4</p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.linkedin.com/company/yournest-venture-capital/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C8473A] transition-colors"
                aria-label="YourNest on LinkedIn (opens in new tab)"
              >
                <LinkedInSolid className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="https://x.com/yournestvc"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C8473A] transition-colors"
                aria-label="YourNest on X (opens in new tab)"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a
                href="mailto:investor.relations@yournest.in"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C8473A] transition-colors"
                aria-label="Email investor relations"
              >
                <Mail className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick Links - two columns */}
          <div className="flex gap-12">
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinksCol1.map((link) => {
                  const isInternal = link.href.startsWith('/') && !link.href.startsWith('//');
                  return (
                    <li key={link.label}>
                      {isInternal ? (
                        <Link
                          to={link.href}
                          className="text-white hover:text-[#C8473A] transition-colors inline-flex items-center gap-1 underline-offset-2 hover:underline"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-[#C8473A] transition-colors inline-flex items-center gap-1 underline-offset-2 hover:underline"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <ul className="space-y-3 mt-9">
                {quickLinksCol2.map((link) => {
                  const isInternal = link.href.startsWith('/') && !link.href.startsWith('//');
                  return (
                    <li key={link.label}>
                      {isInternal ? (
                        <Link
                          to={link.href}
                          className="text-white hover:text-[#C8473A] transition-colors inline-flex items-center gap-1 underline-offset-2 hover:underline"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-[#C8473A] transition-colors inline-flex items-center gap-1 underline-offset-2 hover:underline"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C8473A] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-white text-sm">
                  416, 4th Floor MGF Metropolis Mall, Near M.G. Road Metro Station, Gurugram – 122002 (INDIA)
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#C8473A] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <a href="mailto:investor.relations@yournest.in" className="text-white text-sm hover:text-[#C8473A] transition-colors underline-offset-2 hover:underline" aria-label="Email investor relations at investor.relations@yournest.in">investor.relations@yournest.in</a>
              </div>
            </div>

            {/* Newsletter signup */}
            <div className="mt-10">
              <NewsletterSignup />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-white text-sm text-center md:text-left">
            © {currentYear} YourNest Venture Capital. All rights reserved.
          </p>
          <p className="text-white text-xs text-center md:text-left mt-2">
            Created by YourNest (with a little AI)
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
